import { useMemo, ReactNode } from 'react';
import { TColumn } from '@features/ui';

export interface UseReportTableColumnsConfig<T> {
  // Data for determining available products/groups
  data: T[];

  // Selected filters
  selectedItems?: string[]; // Products, user groups, or categories
  compareToPreviousPeriod?: boolean;
  compareToSamePeriodPreviousYear?: boolean;

  // Data accessors - functions to access nested data in report items
  getAccessField: (item: T) => string;
  getMainDataField: (item: T, key: string) => number;
  getPreviousPeriodField?: (item: T, key: string) => number | undefined;
  getPreviousYearField?: (item: T, key: string) => number | undefined;

  // Column configuration
  itemsField?: string; // Field name in data object (e.g., 'products', 'user_groups')
  columnTitleFormatter?: (key: string) => string; // Custom title formatter
  valueFormatter?: (key: string, value: number) => string | ReactNode; // Format cell values (e.g., currency)
  customColumns?: TColumn<T>[]; // Custom columns to append after the date column (for simple reports)

  // Show totals row
  includeTotalsRow?: boolean;
  totalsRowLabel?: string;
  totalsRowFormatter?: (totals: Record<string, number>) => Record<string, any>;

  // Custom column colors
  mainColor?: string;
  previousPeriodColor?: string;
  previousYearColor?: string;
  totalsColor?: string;

  summaryRow?: Record<string, number>;
}

export interface UseReportTableColumnsReturn<T> {
  columns: TColumn<T>[];
  displayItems: string[];
  tableData: T[];
  totals: Record<string, number>;
}

export const useReportTableColumns = <T extends Record<string, any>>(
  config: UseReportTableColumnsConfig<T>
): UseReportTableColumnsReturn<T> => {
  const {
    data,
    selectedItems = [],
    compareToPreviousPeriod = false,
    compareToSamePeriodPreviousYear = false,
    getAccessField,
    getMainDataField,
    getPreviousPeriodField,
    getPreviousYearField,
    itemsField = '',
    columnTitleFormatter = (key: string) => key.toUpperCase(),
    valueFormatter = (_: string, value: number) => value.toString(),
    customColumns,
    includeTotalsRow = false,
    totalsRowLabel = 'Total',
    totalsRowFormatter,
    mainColor = 'text-white',
    previousPeriodColor = 'text-[#FF6B6B]',
    previousYearColor = 'text-[#4ECDC4]',
    summaryRow,
  } = config;

  const findDataKey = (data: T[]) => {
    return data.reduce(
      (maxItem, current) => {
        const currentCount = current[itemsField]
          ? Object.keys(current[itemsField]).length
          : 0;

        const maxCount = maxItem[itemsField]
          ? Object.keys(maxItem[itemsField]).length
          : 0;

        return currentCount > maxCount ? current : maxItem;
      },
      { [itemsField]: {} }
    );
  };

  // Determine which items to display (all or selected)
  const displayItems = useMemo(() => {
    if (selectedItems.length > 0) {
      return selectedItems;
    }

    // Get all available items from first data row
    if (data.length === 0) return [];

    const item = findDataKey(data);
    const itemsData = item[itemsField];

    if (typeof itemsData === 'object' && itemsData !== null) {
      return Object.keys(itemsData);
    }

    return [];
  }, [selectedItems, data, itemsField]);

  // Calculate totals for each item
  const totals = useMemo(() => {
    const result: Record<string, number> = {};

    if (summaryRow) {
      return summaryRow;
    }

    // For custom columns mode, calculate totals based on data field extraction
    if (customColumns && customColumns.length > 0) {
      // Try to extract numeric fields from data and sum them
      if (data.length > 0) {
        const firstItem = data[0];

        Object.keys(firstItem).forEach((key) => {
          const value = firstItem[key];

          // Only sum numeric fields (excluding the date field)
          if (typeof value === 'number' && key !== getAccessField.name) {
            result[key] = data.reduce((sum, row) => sum + (row[key] || 0), 0);
          }
        });
      }
    }

    displayItems.forEach((item) => {
      // Main data total
      result[item] = data.reduce(
        (sum, row) => sum + (getMainDataField(row, item) || 0),
        0
      );

      // Previous period total
      if (compareToPreviousPeriod && getPreviousPeriodField) {
        result[`${item}_previous`] = data.reduce(
          (sum, row) => sum + (getPreviousPeriodField(row, item) || 0),
          0
        );
      }

      // Previous year total
      if (compareToSamePeriodPreviousYear && getPreviousYearField) {
        result[`${item}_previous_year`] = data.reduce(
          (sum, row) => sum + (getPreviousYearField(row, item) || 0),
          0
        );
      }
    });

    return result;
  }, [
    data,
    displayItems,
    getMainDataField,
    getPreviousPeriodField,
    getPreviousYearField,
    compareToPreviousPeriod,
    compareToSamePeriodPreviousYear,
    customColumns,
    getAccessField,
  ]);

  // Generate table data with totals row if needed
  const tableData = useMemo(() => {
    if (!includeTotalsRow) return data;

    // Get the date field name from the first data item
    const dateFieldName =
      data.length > 0
        ? Object.keys(data[0]).find(
            (key) => getAccessField(data[0]) === data[0][key]
          ) || 'date'
        : 'date';

    const totalsRow: any = {
      [dateFieldName]: totalsRowLabel,
    };

    // Apply custom formatter if provided
    const formattedTotals = totalsRowFormatter
      ? totalsRowFormatter(totals)
      : totals;

    if (!itemsField) {
      return [
        ...data,
        { [dateFieldName]: totalsRowLabel, ...formattedTotals } as T,
      ];
    }

    // For custom columns mode, add all calculated totals to the row
    if (customColumns && customColumns.length > 0) {
      Object.keys(formattedTotals).forEach((key) => {
        totalsRow[key] = formattedTotals[key];
      });
    }

    // Build totals row structure matching the data structure for dynamic columns
    const totalsDataField: any = {};

    displayItems.forEach((item) => {
      totalsDataField[item] = formattedTotals[item] || 0;
    });
    totalsRow[itemsField] = totalsDataField;

    // Add previous period data if needed
    if (compareToPreviousPeriod && getPreviousPeriodField) {
      const previousDataField: any = {};

      displayItems.forEach((item) => {
        previousDataField[item] = formattedTotals[`${item}_previous`] || 0;
      });
      totalsRow[`${itemsField}_previous`] = previousDataField;
    }

    // Add previous year data if needed
    if (compareToSamePeriodPreviousYear && getPreviousYearField) {
      const previousYearDataField: any = {};

      displayItems.forEach((item) => {
        previousYearDataField[item] =
          formattedTotals[`${item}_previous_year`] || 0;
      });
      totalsRow[`${itemsField}_previous_year`] = previousYearDataField;
    }

    return [...data, totalsRow as T];
  }, [
    data,
    includeTotalsRow,
    displayItems,
    totals,
    itemsField,
    totalsRowLabel,
    getAccessField,
    compareToPreviousPeriod,
    compareToSamePeriodPreviousYear,
    getPreviousPeriodField,
    getPreviousYearField,
    customColumns,
    totalsRowFormatter,
  ]);

  // Generate dynamic columns
  const columns = useMemo((): TColumn<T>[] => {
    const cols: TColumn<T>[] = [
      {
        title: '#',
        render: (item) => {
          if (!item) return <p className="text-white">-</p>;
          const dateValue = getAccessField(item);
          const isTotalsRow = dateValue === totalsRowLabel;

          return (
            <p className={`text-white ${isTotalsRow ? `font-bold` : ''}`}>
              {dateValue ?? '-'}
            </p>
          );
        },
      },
    ];

    // If custom columns provided, append them and return
    if (customColumns && customColumns.length > 0) {
      cols.push(...customColumns);
    }

    // Dynamic columns mode - generate columns for each item
    displayItems.forEach((itemKey) => {
      // Main column
      cols.push({
        title: columnTitleFormatter(itemKey),
        className: 'text-right',
        render: (item) => {
          if (!item) return <p className={mainColor}>0</p>;
          const dateValue = getAccessField(item);
          const isTotalsRow = dateValue === totalsRowLabel;
          const value = getMainDataField(item, itemKey);

          return (
            <p className={`${isTotalsRow ? `font-bold` : mainColor}`}>
              {valueFormatter(itemKey, value ?? 0)}
            </p>
          );
        },
      });

      // Previous period column
      if (compareToPreviousPeriod && getPreviousPeriodField) {
        cols.push({
          title: `${columnTitleFormatter(itemKey)} (PREVIOUS PERIOD)`,
          className: 'text-right',
          render: (item) => {
            if (!item) return <p className={previousPeriodColor}>0</p>;
            const dateValue = getAccessField(item);
            const isTotalsRow = dateValue === totalsRowLabel;
            const value = getPreviousPeriodField(item, itemKey);

            return (
              <p
                className={`${isTotalsRow ? `font-bold` : previousPeriodColor}`}
              >
                {valueFormatter(itemKey, value ?? 0)}
              </p>
            );
          },
        });
      }

      if (compareToSamePeriodPreviousYear && getPreviousYearField) {
        cols.push({
          title: `${columnTitleFormatter(itemKey)} (PREVIOUS YEAR)`,
          className: 'text-right',
          render: (item) => {
            if (!item) return <p className={previousYearColor}>0</p>;
            const dateValue = getAccessField(item);
            const isTotalsRow = dateValue === totalsRowLabel;
            const value = getPreviousYearField(item, itemKey);

            return (
              <p className={`${isTotalsRow ? `font-bold` : previousYearColor}`}>
                {valueFormatter(itemKey, value ?? 0)}
              </p>
            );
          },
        });
      }
    });

    return cols;
  }, [
    displayItems,
    getAccessField,
    getMainDataField,
    getPreviousPeriodField,
    getPreviousYearField,
    columnTitleFormatter,
    valueFormatter,
    compareToPreviousPeriod,
    compareToSamePeriodPreviousYear,
    totalsRowLabel,
    mainColor,
    previousPeriodColor,
    previousYearColor,
    customColumns,
  ]);

  return {
    columns,
    displayItems,
    tableData,
    totals,
  };
};
